import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";
import WidgetPlaceholder from "./WidgetPlaceholder";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";
import { PA_GREEN } from "./PracticeListPanel";

// ─── Types — matches the live chart_type: "bubble_quadrant_chart" response ────

type BubblePoint = {
  name: string;
  insurance_type?: string;
  x: number;
  y: number;
  size: number;
  volume?: number;
  pa_required_pct?: number;
  approval_pct?: number;
  decided?: number;
  tier: string;
  // Per-point color always comes from the backend — never computed
  // client-side (matches the rest of this codebase's bar/pie widgets).
  color: string;
};

type AxisSpec = { key?: string; label?: string };

type Segment = { name: string; count: number; color: string; rule: string };

type Quadrant = {
  tier: string;
  color: string;
  x_range: [number, number];
  y_range: [number, number];
};

type FilterOption = number | string | { value: number | string; label: string };

type FilterSpec = {
  key: string;
  label: string;
  control_type?: string;
  type?: string;
  default?: number | string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: FilterOption[];
};

type BubbleQuadrantMetadata = {
  x_axis?: AxisSpec;
  y_axis?: AxisSpec;
  size_axis?: AxisSpec;
  top_n?: number | string;
  segments?: Segment[];
  quadrants?: Quadrant[];
  // The exact boundary between quadrants — same values the quadrant
  // rectangles' shared edges sit on, drawn as an explicit divider line
  // since the tint alone wasn't a clear enough boundary.
  thresholds?: { pa_required_threshold?: number; approval_threshold?: number };
  filters?: FilterSpec[];
  [key: string]: unknown;
};

// Sibling of .data/.metadata, not nested under metadata. `payers` and
// `total_cases` are scoped to the top_n actually returned (total_cases equals
// the sum of the returned rows' volume) — `total_payers` is the only figure
// here that covers the whole data set. The rate strings arrive pre-formatted
// ("66.6%"), already rounded by the backend.
type BubbleQuadrantKpi = {
  payers?: number;
  total_payers?: number;
  total_cases?: number;
  total_decided?: number;
  total_approved?: number;
  total_pa_required?: number;
  pa_approval_rate?: string;
  pa_required_rate?: string;
};

type BubbleQuadrantData = {
  data?: BubblePoint[];
  metadata?: BubbleQuadrantMetadata;
  kpi?: BubbleQuadrantKpi;
  // Siblings of .data, not nested under .metadata.
  has_download_options?: boolean;
  download_options?: WidgetDownloadOptions;
};

type Props = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
  // This widget self-fetches, so WidgetRenderer's own query for it is disabled
  // and it never sees has_download_options to decide whether the header
  // hamburger should render. Report it back up once known — same pattern as
  // AverageBarWidget / CustomTableWidget.
  onDownloadOptionsChange?: (hasDownloadOptions: boolean) => void;
};

// A chart with quadrant-shading rectangles stashed on it between redraws so
// they can be cleared before redrawing (Highcharts has no built-in "shade an
// arbitrary x+y rectangle" primitive — plotBands only span one full axis).
type ChartWithQuadrantRects = Highcharts.Chart & {
  _quadrantRects?: Highcharts.SVGElement[];
};

const unwrap = (raw: unknown): BubbleQuadrantData => {
  if (Array.isArray(raw)) return { data: raw as BubblePoint[] };
  if (raw && typeof raw === "object") return raw as BubbleQuadrantData;
  return {};
};

const optVal = (o: FilterOption): number | string =>
  typeof o === "object" && o !== null && "value" in o ? o.value : o;

const optLabel = (o: FilterOption): string =>
  typeof o === "object" && o !== null && "label" in o
    ? String(o.label)
    : String(o);

export default function BubbleQuadrantWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
  onDownloadOptionsChange,
}: Props) {
  const [draftFilters, setDraftFilters] = useState<
    Record<string, number | string>
  >({});
  const [appliedWidgetFilters, setAppliedWidgetFilters] = useState<
    Record<string, number | string>
  >({});

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: [
      "widget",
      dashboardName,
      widgetTemplateId,
      appliedFilters,
      appliedWidgetFilters,
    ],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        {
          filters: appliedFilters,
          widget_filters:
            Object.keys(appliedWidgetFilters).length > 0
              ? appliedWidgetFilters
              : undefined,
        },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const parsed = useMemo(
    () => unwrap(canFetch ? queryData : data),
    [canFetch, queryData, data],
  );
  const points = parsed.data ?? [];
  const meta = parsed.metadata ?? {};
  const kpi = parsed.kpi;
  const specFilters = meta.filters ?? [];

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={
        parsed.has_download_options ? parsed.download_options : undefined
      }
    />
  );

  // KPI strip, mirroring the site quadrant's (KpiQuadrantWidget's KpiStrip).
  // Payers/Total Cases come straight from the backend's kpi block, never
  // summed here. Both describe the payers currently plotted, so they move with
  // the Show selector together — the whole-data-set figure (total_payers) goes
  // in the tooltip instead, since pairing 2,440 payers with a top-20 case count
  // would read as a total when it isn't.
  // The two rate tiles are the applied thresholds from metadata, not the kpi
  // block's actual rates, and not draftFilters: they mark where the quadrant
  // dividers are drawn, so dragging a slider without pressing Apply must leave
  // both the chart and these tiles untouched.
  const kpiTiles = useMemo(() => {
    const pct = (v?: number): string =>
      typeof v === "number" ? `${v.toFixed(1)}%` : "—";
    const num = (v?: number): string =>
      typeof v === "number" ? v.toLocaleString() : "—";
    const t = meta.thresholds ?? {};
    return [
      {
        label: "Payers",
        value: num(kpi?.payers),
        hint:
          typeof kpi?.total_payers === "number"
            ? `of ${kpi.total_payers.toLocaleString()} payers in the data set`
            : "Payers plotted",
      },
      {
        label: "Total Cases",
        value: num(kpi?.total_cases),
        hint: "Cases across the payers plotted",
      },
      {
        label: "PA Required",
        value: pct(t.pa_required_threshold),
        hint: "Applied PA required rate threshold — the vertical quadrant divider",
      },
      {
        label: "PA Approved",
        value: pct(t.approval_threshold),
        color: PA_GREEN,
        hint: "Applied approval rate threshold — the horizontal quadrant divider",
      },
    ];
  }, [kpi, meta.thresholds]);

  useEffect(() => {
    onDownloadOptionsChange?.(!!parsed.has_download_options);
  }, [parsed.has_download_options, onDownloadOptionsChange]);

  const topNFilter = specFilters.find((f) => f.key === "top_n");
  const thresholdFilters = specFilters.filter((f) => f.key !== "top_n");

  const draftVal = (f: FilterSpec): number | string =>
    draftFilters[f.key] ?? f.default ?? 0;

  const hasPendingSliderChange = thresholdFilters.some(
    (f) => draftFilters[f.key] !== undefined,
  );

  const quadrants = useMemo(() => meta.quadrants ?? [], [meta.quadrants]);
  const segments = useMemo(() => meta.segments ?? [], [meta.segments]);
  const segmentRule = (tier: string): string =>
    segments.find((s) => s.name === tier)?.rule ?? "";

  const bubbleData = useMemo(
    () =>
      points.map((p) => ({
        x: p.x,
        y: p.y,
        z: p.size,
        name: p.name,
        color: p.color,
        custom: {
          approvalPct: p.approval_pct ?? p.y,
          paRequiredPct: p.pa_required_pct ?? p.x,
          volume: p.volume ?? p.size,
          tier: p.tier,
          rule: segmentRule(p.tier),
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points, segments],
  );

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        type: "bubble",
        style: { fontFamily: "inherit" },
        marginTop: 20,
        events: {
          // Draws the 4 quadrant background tints — plotBands can only shade
          // a full-width/full-height stripe, not a bounded x+y rectangle, so
          // this draws them directly with the renderer and re-draws on every
          // redraw (resize, data change) since Highcharts doesn't persist
          // custom shapes across those the way it does axis config.
          render: function (this: ChartWithQuadrantRects) {
            this._quadrantRects?.forEach((rect) => rect.destroy());
            const xAxis = this.xAxis[0];
            const yAxis = this.yAxis[0];
            if (!xAxis || !yAxis) return;
            // The 4 quadrants' ranges only cover the real 0-100 rate data —
            // the axis itself is padded out to -5/110 so edge bubbles aren't
            // clipped, which left a plain unshaded margin between the data
            // edge and the axis edge. Extending only the OUTER edge of the
            // combined quadrant grid (not the shared inner threshold
            // boundary) to the axis's actual min/max fills that margin too.
            const gridXMin = Math.min(...quadrants.map((q) => q.x_range[0]));
            const gridXMax = Math.max(...quadrants.map((q) => q.x_range[1]));
            const gridYMin = Math.min(...quadrants.map((q) => q.y_range[0]));
            const gridYMax = Math.max(...quadrants.map((q) => q.y_range[1]));
            this._quadrantRects = quadrants.map((q) => {
              const x0 =
                q.x_range[0] === gridXMin
                  ? (xAxis.min ?? q.x_range[0])
                  : q.x_range[0];
              const x1 =
                q.x_range[1] === gridXMax
                  ? (xAxis.max ?? q.x_range[1])
                  : q.x_range[1];
              const y0 =
                q.y_range[0] === gridYMin
                  ? (yAxis.min ?? q.y_range[0])
                  : q.y_range[0];
              const y1 =
                q.y_range[1] === gridYMax
                  ? (yAxis.max ?? q.y_range[1])
                  : q.y_range[1];
              const px1 = xAxis.toPixels(x0, false);
              const px2 = xAxis.toPixels(x1, false);
              // y pixels run opposite to data direction (0 is top of SVG),
              // so the smaller y-value maps to the larger pixel coordinate.
              const py1 = yAxis.toPixels(y1, false);
              const py2 = yAxis.toPixels(y0, false);
              return this.renderer
                .rect(
                  Math.min(px1, px2),
                  Math.min(py1, py2),
                  Math.abs(px2 - px1),
                  Math.abs(py2 - py1),
                )
                .attr({ fill: q.color, opacity: 0.16, zIndex: 0 })
                .add();
            });
          },
        },
      },
      title: { text: "" },
      credits: { enabled: false },
      accessibility: { enabled: false },
      legend: { enabled: false },
      // This widget is in WidgetRenderer's NO_MENU_TYPES (no custom header
      // hamburger renders for it), so there's no icon to tuck a native
      // Highcharts export button behind — leaving exporting enabled would
      // just show Highcharts' own real button floating in the chart itself.
      exporting: { enabled: false },
      xAxis: {
        title: { text: meta.x_axis?.label ?? "" },
        min: -5,
        max: 110,
        tickInterval: 20,
        startOnTick: false,
        endOnTick: false,
        gridLineWidth: 1,
        gridLineColor: "#D7DEE7",
        // Highcharts' xAxis default (visible axis line + tick marks below
        // each label) reads differently from the yAxis default (neither) —
        // matched explicitly here so both sides look the same.
        lineWidth: 0,
        tickLength: 0,
        labels: {
          formatter: function () {
            return `${this.value}%`;
          },
        },
      },
      yAxis: {
        title: { text: meta.y_axis?.label ?? "" },
        min: -5,
        max: 110,
        tickInterval: 20,
        startOnTick: false,
        endOnTick: false,
        gridLineWidth: 1,
        gridLineColor: "#D7DEE7",
        lineWidth: 0,
        tickLength: 0,
        labels: {
          formatter: function () {
            return `${this.value}%`;
          },
        },
      },
      tooltip: {
        useHTML: true,
        // Highcharts draws its own background/border/shadow behind useHTML
        // content, sized from a bbox measurement taken before the actual
        // HTML (table + wrapped text) finishes laying out. That estimate can
        // undershoot the real content height — most visible once the chart
        // is tall enough that Highcharts positions the tooltip above the
        // point instead of below — so the native box ends up shorter than
        // our HTML, and whatever doesn't fit (the title) renders outside it.
        // Disabling the native chrome and drawing the box ourselves in the
        // returned HTML means the visible box IS the content's real bounding
        // box, so it can never desync from it.
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        formatter: function (this: any) {
          const c = this.point?.custom ?? {};
          return `<div style="background:#fff;border-radius:6px;box-shadow:0 2px 10px rgba(15,23,42,0.22);padding:8px 10px;min-width:190px;">
            <div style="font-size:12px;font-weight:700;margin-bottom:4px;">${this.point.name}</div>
            <table style="font-size:11px;border-collapse:collapse;width:100%">
              <tr><td style="color:#64748B;padding-right:10px">Approval rate</td><td style="font-weight:600">${c.approvalPct}%</td></tr>
              <tr><td style="color:#64748B;padding-right:10px">PA required rate</td><td style="font-weight:600">${c.paRequiredPct}%</td></tr>
              <tr><td style="color:#64748B;padding-right:10px">Case volume</td><td style="font-weight:600">${Number(c.volume ?? 0).toLocaleString()}</td></tr>
            </table>
            <div style="margin-top:6px;font-size:10.5px;color:#64748B;">${c.tier}${c.rule ? ` (${c.rule})` : ""}</div>
          </div>`;
        },
      },
      plotOptions: {
        bubble: {
          minSize: 14,
          maxSize: 70,
          animation: false,
          marker: {
            fillOpacity: 0.85,
            lineWidth: 1,
            lineColor: "rgba(255,255,255,0.9)",
          },
          states: { hover: { halo: { size: 3 } } },
        },
      },
      series: [
        {
          type: "bubble",
          name: "Payers",
          data: bubbleData,
        },
      ],
    }),
    [meta.x_axis, meta.y_axis, quadrants, bubbleData],
  );

  if (canFetch ? isFetching : isLoading) {
    return <WidgetPlaceholder height={480} />;
  }

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {downloadMenu}

      <div
        className="d-flex align-items-center flex-wrap gap-2 border-bottom px-3"
        style={{ paddingTop: 6, paddingBottom: 6, flexShrink: 0 }}
      >
        {kpiTiles.map((tile) => (
          <div
            key={tile.label}
            className="d-flex flex-column align-items-center px-2"
            title={tile.hint}
          >
            <span
              style={{
                fontSize: 10,
                color: "var(--bs-secondary-color)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              {tile.label}
            </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: tile.color ?? "var(--bs-body-color)",
                lineHeight: 1.2,
              }}
            >
              {tile.value}
            </span>
          </div>
        ))}
      </div>

      {topNFilter && (
        <div
          className="d-flex align-items-center gap-2 border-bottom px-3"
          style={{ paddingTop: 10, paddingBottom: 10, flexShrink: 0 }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {topNFilter.label}
          </span>
          <select
            className="form-select form-select-sm"
            style={{
              width: "auto",
              fontSize: 13,
              minWidth: 80,
              cursor: "pointer",
            }}
            value={String(
              draftFilters.top_n ??
                appliedWidgetFilters.top_n ??
                topNFilter.default ??
                10,
            )}
            onChange={(e) => {
              const value = e.target.value;
              setDraftFilters((prev) => ({ ...prev, top_n: value }));
              setAppliedWidgetFilters((prev) => ({ ...prev, top_n: value }));
            }}
          >
            {(topNFilter.options ?? []).map((opt) => {
              const v = optVal(opt);
              return (
                <option key={String(v)} value={String(v)}>
                  {optLabel(opt)}
                </option>
              );
            })}
          </select>
          <span style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}>
            by case volume, descending
          </span>
        </div>
      )}

      {thresholdFilters.length > 0 && (
        <div
          className="d-flex flex-wrap align-items-end gap-4 border-bottom px-3"
          style={{ paddingTop: 10, paddingBottom: 10, flexShrink: 0 }}
        >
          {thresholdFilters.map((f) => {
            const val = Number(draftVal(f));
            const min = f.min ?? 0;
            const max = f.max ?? 100;
            const pct = ((val - min) / (max - min)) * 100;
            return (
              <div key={f.key} style={{ flex: "1 1 200px", minWidth: 160 }}>
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: 13, color: "var(--bs-body-color)" }}>
                    {f.label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>
                    {val.toFixed(1)}
                    {f.unit ?? ""}
                  </span>
                </div>
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={f.step ?? 1}
                  value={val}
                  onChange={(e) =>
                    setDraftFilters((prev) => ({
                      ...prev,
                      [f.key]: Number(e.target.value),
                    }))
                  }
                  style={{
                    width: "100%",
                    height: 5,
                    appearance: "none",
                    WebkitAppearance: "none",
                    background: `linear-gradient(to right, #545caa ${pct}%, var(--bs-border-color, #dee2e6) ${pct}%)`,
                    borderRadius: 4,
                    outline: "none",
                    cursor: "pointer",
                    accentColor: "#545caa",
                  }}
                />
              </div>
            );
          })}
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!hasPendingSliderChange}
            onClick={() =>
              setAppliedWidgetFilters((prev) => ({ ...prev, ...draftFilters }))
            }
          >
            Apply
          </button>
        </div>
      )}

      {segments.length > 0 && (
        <div
          className="d-flex flex-wrap align-items-center gap-3 px-3"
          style={{ paddingTop: 8, paddingBottom: 8, flexShrink: 0 }}
        >
          {segments.map((s) => (
            <div key={s.name} className="d-flex align-items-center gap-2">
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: s.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12.5 }}>
                {s.name}{" "}
                <span style={{ color: "var(--bs-secondary-color)" }}>
                  ({s.rule})
                </span>{" "}
                ({s.count})
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex-fill" style={{ minHeight: 0 }}>
        {points.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center h-100"
            style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
          >
            No payer data available.
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            containerProps={{ style: { width: "100%", height: "100%" } }}
          />
        )}
      </div>
    </div>
  );
}
